import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { callGoogleAdsProxy, GOOGLE_ADS_API_VERSION_PATH } from "../services/google-ads-proxy-client";

import type { IGoogleAdsAccountOption } from "./use-google-ads-accounts";
import { useGoogleAdsConnection } from "./use-google-ads-oauth";

export interface IGoogleAdsAdGroupListItem {
    id: string;
    name: string;
    status: string;
}

interface IGoogleAdsAdGroupSearchResponse {
    results?: { adGroup?: { id?: string; name?: string; status?: string } }[];
}

const buildAdGroupsQuery = (campaignId: string): string =>
    `SELECT ad_group.id, ad_group.name, ad_group.status FROM ad_group WHERE campaign.id = ${campaignId} AND ad_group.status != REMOVED`;

const fetchGoogleAdsAdGroups = async (
    account: IGoogleAdsAccountOption,
    campaignId: string,
): Promise<IGoogleAdsAdGroupListItem[]> => {
    const adGroupsResponse = await callGoogleAdsProxy<IGoogleAdsAdGroupSearchResponse>({
        method: "POST",
        path: `${GOOGLE_ADS_API_VERSION_PATH}/customers/${account.id}/googleAds:search`,
        body: { query: buildAdGroupsQuery(campaignId) },
        loginCustomerId: account.loginCustomerId,
    });

    return (adGroupsResponse.results ?? [])
        .map((result) => result.adGroup)
        .filter((adGroup): adGroup is { id: string; name?: string; status?: string } => Boolean(adGroup?.id))
        .map((adGroup) => ({
            id: adGroup.id,
            name: adGroup.name ?? "",
            status: adGroup.status ?? "",
        }));
};

export const useGoogleAdsAdGroups = (
    account: IGoogleAdsAccountOption | null,
    campaignId: string | null,
): UseQueryResult<IGoogleAdsAdGroupListItem[]> => {
    const { isConnected, connectionId } = useGoogleAdsConnection();

    return useQuery({
        queryKey: ["googleAdsAdGroups", connectionId, account?.id, campaignId],
        queryFn: () => fetchGoogleAdsAdGroups(account as IGoogleAdsAccountOption, campaignId as string),
        enabled: isConnected && Boolean(account) && Boolean(campaignId),
        retry: false,
    });
};
